// Zero-dependency so both `env.ts` and `domain-rewrites.ts` can use it:
// `domain-rewrites.ts` imports from `env.ts`, so the predicate cannot live in
// either without creating an import cycle.
//
// Per-PR preview environments are served at `pr-<n>[-<target>].staging.mnm.st`
// (MIN-4503). Several places derive behaviour from the request hostname, and
// each one that does not know about that label gets it wrong in a different
// way — a "Domain not configured" render failure from the (since removed)
// Medusa client in one case, silently editing the PRODUCTION dataset in another. One shared
// predicate keeps those verdicts from drifting.
//
// Deliberately narrow: only a leading `pr-<digits>` label, and only when what
// follows is `staging.`. A real marketplace subdomain cannot match.
const PREVIEW_ENV_HOSTNAME_RE = /^pr-\d+(?:-[a-z0-9-]+)?\.(staging\..+)$/

/** True if `hostname` is a per-PR preview environment hostname. */
export function isPreviewEnvHostname(hostname: string): boolean {
  return PREVIEW_ENV_HOSTNAME_RE.test(hostname)
}

/**
 * Strip the per-PR label, leaving the hostname the preview stands in for
 * (`pr-42-website.staging.mnm.st` -> `staging.mnm.st`). Returns `hostname`
 * unchanged when it is not a preview hostname.
 */
export function stripPreviewEnvLabel(hostname: string): string {
  return PREVIEW_ENV_HOSTNAME_RE.exec(hostname)?.[1] ?? hostname
}

/**
 * The Sanity dataset the browser should talk to on `hostname`: `staging` for
 * staging and its per-PR previews, `production` everywhere else.
 */
export function sanityDatasetForHostname(hostname: string): "staging" | "production" {
  return stripPreviewEnvLabel(hostname).startsWith("staging.") ? "staging" : "production"
}
